/*
 * Copyright © 2018-2026, GlobalVET AB
 *
 * All rights reserved. No part or the whole of this source code and the compiled program
 * may be reproduced, copied, distributed, disseminated to the public, adapted or
 * transmitted in any form or by any means, including photocopying, recording, or other
 * electronic or mechanical methods, without the prior written permission of GlobalVET AB.
 * This source code and the compiled program may only be used for the purposes of GlobalVET AB.
 * This source code and the compiled program shall be kept confidential and shall not be made
 * public or made available or disclosed to any unauthorized person. Any dispute or claim
 * arising out of the breach of these provisions shall be governed by and construed in
 * accordance with the laws of Sweden.
 */

import React, { useState, ReactElement, useEffect } from "react";
import { strings } from "../../common/Strings/Strings";
import { PageProps } from "../../models/PageProps";
import { SiteManagerPage } from "./SiteManagerPage";
import MimirApi from "../../api/MimirApi";
import logger from "../../util/logger";
import Button from "../../components/Button";

function MimirTestPage(props: PageProps): ReactElement {
  const { setPageLoading } = props;

  // State for each test
  const [statusInfo, setStatusInfo] = useState<string | null>(null);
  const [checkingStatus, setCheckingStatus] = useState(false);

  const [transcribeInfo, setTranscribeInfo] = useState<string | null>(null);
  const [testingTranscribe, setTestingTranscribe] = useState(false);
  const [testTranscriptionId, setTestTranscriptionId] = useState<string | null>(null);

  const [pollInfo, setPollInfo] = useState<string | null>(null);
  const [testingPoll, setTestingPoll] = useState(false);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  useEffect(() => {
    setPageLoading(false);
  }, [setPageLoading]);

  // Test 1: Check API Status
  const testStatus = async () => {
    setCheckingStatus(true);
    setStatusInfo(null);

    try {
      const res = await MimirApi.getStatus();
      setStatusInfo(`✓ Status: ${res.status} ${res.statusText}\nResponse: ${JSON.stringify(res.data, null, 2)}`);
    } catch (err: any) {
      setStatusInfo(`✗ Error: ${err.response?.status ?? "Network"} ${err.response?.statusText ?? err.message}`);
      logger.error("[Mimir] Status check failed:", err);
    } finally {
      setCheckingStatus(false);
    }
  };

  // Test 2: Upload MP3 File and get transcription ID
  const testTranscribe = async () => {
    if (!selectedFile) {
      setTranscribeInfo("✗ Please select an MP3 file first.");
      return;
    }

    setTestingTranscribe(true);
    setTranscribeInfo(null);
    setTestTranscriptionId(null);

    try {
      // Get presigned URL
      const { data } = await MimirApi.getPresignedUploadUrl(selectedFile.name);
      const { upload_url, transcription_id } = data;

      // Upload the file
      await MimirApi.uploadAudioToPresignedUrl(upload_url, selectedFile);

      setTestTranscriptionId(transcription_id);
      setTranscribeInfo(
        `✓ Upload complete!\n  transcription_id: ${transcription_id}\n  File: ${selectedFile.name}\n\nUse this ID in section 3 to poll for results.`
      );
    } catch (err: any) {
      setTranscribeInfo(
        `✗ Transcribe error: ${err.response?.status ?? "Network"} ${err.response?.statusText ?? err.message}`
      );
      logger.error("[Mimir] Transcribe test failed:", err);
    } finally {
      setTestingTranscribe(false);
    }
  };

  // Test 3: Poll for Transcription Result
  const testPoll = async () => {
    const idToTest = testTranscriptionId || "test";
    setTestingPoll(true);
    setPollInfo(null);

    try {
      const response = await MimirApi.pollTranscriptionResult(idToTest);
      setPollInfo(`✓ Poll OK:\n  transcription_id: ${idToTest}\n  response: ${JSON.stringify(response.data, null, 2)}`);
    } catch (err: any) {
      setPollInfo(
        `✗ Poll error: ${err.response?.status ?? "Network"} ${err.response?.statusText ?? err.message}\n  ID tested: ${idToTest}`
      );
      logger.error("[Mimir] Poll test failed:", err);
    } finally {
      setTestingPoll(false);
    }
  };

  return (
    <div className="px-4 lg:px-6 py-6">
      <h1 className="text-2xl font-bold mb-6">Mimir API Manual Testing</h1>

      {/* Test 1: Status */}
      <div className="bg-white border rounded-lg p-4 mb-4">
        <h3 className="font-semibold text-lg mb-3">1. Test Status Endpoint</h3>
        <p className="text-sm text-gray-600 mb-3">GET /status - Check if the Mimir API is running</p>
        <Button
          minW
          variant="primary"
          onClick={testStatus}
          disabled={checkingStatus}
        >
          {checkingStatus ? "Checking..." : "Test API Status"}
        </Button>
        {statusInfo && (
          <pre className="mt-3 p-3 bg-gray-50 rounded text-sm font-mono whitespace-pre-wrap overflow-auto">
            {statusInfo}
          </pre>
        )}
      </div>

      {/* Test 2: Upload MP3 File */}
      <div className="bg-white border rounded-lg p-4 mb-4">
        <h3 className="font-semibold text-lg mb-3">2. Upload MP3 File</h3>
        <p className="text-sm text-gray-600 mb-3">Upload MP3 file to /transcribe endpoint and get transcription_id</p>
        <div className="mb-3">
          <label className="text-sm text-gray-600 block mb-2">Select MP3 file:</label>
          <input
            type="file"
            accept="audio/mp3,audio/mpeg"
            onChange={(e) => {
              if (e.target.files && e.target.files.length > 0) {
                setSelectedFile(e.target.files[0]);
              }
            }}
            className="text-sm text-gray-600"
          />
          {selectedFile && (
            <p className="text-sm text-gray-500 mt-2">Selected: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)</p>
          )}
        </div>
        <Button
        minW
          variant="primary"
          onClick={testTranscribe}
          disabled={testingTranscribe || !selectedFile}
        >
          {testingTranscribe ? "Testing..." : "Test /transcribe"}
        </Button>
        {transcribeInfo && (
          <pre className="mt-3 p-3 bg-gray-50 rounded text-sm font-mono whitespace-pre-wrap overflow-auto">
            {transcribeInfo}
          </pre>
        )}
      </div>

      {/* Test 3: Poll */}
      <div className="bg-white border rounded-lg p-4 mb-4">
        <h3 className="font-semibold text-lg mb-3">3. Poll for Result</h3>
        <p className="text-sm text-gray-600 mb-3">GET /transcribe/:id - Poll for transcription result</p>
        <div className="flex items-center gap-2 mb-3">
          <label className="text-sm text-gray-600">Transcription ID:</label>
          <input
            type="text"
            value={testTranscriptionId || ""}
            onChange={(e) => setTestTranscriptionId(e.target.value)}
            placeholder="Enter transcription ID to test"
            className="flex-1 px-3 py-2 border rounded"
          />
        </div>
        <Button
        minW
          variant="primary"
          onClick={testPoll}
          disabled={testingPoll}
        >
          {testingPoll ? "Polling..." : "Poll for Result"}
        </Button>
        {pollInfo && (
          <pre className="mt-3 p-3 bg-gray-50 rounded text-sm font-mono whitespace-pre-wrap overflow-auto">
            {pollInfo}
          </pre>
        )}
      </div>
    </div>
  );
}

export default SiteManagerPage(MimirTestPage, strings.MIMIR_TEST);